import {
  Search,
  Bell,
  Sun,
  Moon,
  Grid3X3,
  Menu,
  ChevronDown,
  LogOut,
  Settings,
  KeyRound,
  UserCircle2,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { logout } from "@/lib/auth";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { languages } from "@/i18n";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

interface TopBarProps {
  onMenuClick: () => void;
  onSearchClick: () => void; // ✅ add
  onLogout?: () => void;
  onProfile?: () => void;
  onChangePassword?: () => void;
}

export function TopBar({
  onMenuClick,
  onSearchClick,
  onProfile,
  onChangePassword,
}: TopBarProps) {
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();

  // ✅ RTL/LTR
  const dir =
    (document.documentElement.getAttribute("dir") as "ltr" | "rtl") ||
    i18n.dir() ||
    "ltr";
  const isRTL = dir === "rtl";

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];
  const [userName, setUserName] = useState<string>("Account");

  useEffect(() => {
    const stored =
      localStorage.getItem("userData") || sessionStorage.getItem("userData");

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.name) setUserName(parsed.name);
      } catch {}
    }
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  const searchIconSide = useMemo(() => (isRTL ? "right-3" : "left-3"), [isRTL]);
  const searchPadding = useMemo(() => (isRTL ? "pr-10" : "pl-10"), [isRTL]);
  const dotSide = useMemo(() => (isRTL ? "left-1.5" : "right-1.5"), [isRTL]);
   const navigate = useNavigate();

  return (
    <header
      className="h-16 border-b border-border bg-card flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30"
      dir={dir}
    >
      {/* Left */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenuClick}>
          <Menu className="h-5 w-5" />
        </Button>

        
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1 sm:gap-2">
        {/* ✅ Mobile search icon opens global search menu */}
       

        {/* Language Switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" title={t("topbar.language")}>
              <span className="text-base leading-none">{currentLang.flag}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {languages.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                className={i18n.language === lang.code ? "bg-accent" : ""}
              >
                <span className="me-2">{lang.flag}</span>
                {lang.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        <Button variant="ghost" size="icon" className="hidden sm:inline-flex">
          <Grid3X3 className="h-5 w-5" />
        </Button>

        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {/* ✅ RTL-aware dot */}
          <span className={`absolute top-1.5 ${dotSide} h-2 w-2 rounded-full bg-destructive`} />
        </Button>

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="ml-1 sm:ml-2 h-10 px-2 rounded-full flex items-center gap-2"
            >
              <span className="hidden sm:inline-flex items-center gap-1 text-sm">
                {userName}
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </span>

              <span className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
                <span className="text-sm font-bold text-primary-foreground">
                  {userName.charAt(0).toUpperCase()}
                </span>
              </span>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={onProfile}>
              <UserCircle2 className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => navigate("/reset-password")}>
              <KeyRound className="mr-2 h-4 w-4" />
              Change Password
            </DropdownMenuItem>

            <DropdownMenuItem onClick={toggleTheme}>
              {theme === "dark" ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
              Theme: {theme === "dark" ? "Light" : "Dark"}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}